import { all, call, put, takeEvery } from 'redux-saga/effects';
import { uniq, uniqBy, groupBy } from 'lodash';
import callApi from 'utils/callApi';
import formatSinglePlural from 'utils/formatSinglePlural';
import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';
import { requestWorkflowBulk, requestBulk } from 'redux/modules/bulk';
import { FETCH_INVENTORY_SUCCESS } from 'redux/modules/inventory';
import orderStatusesAutoChange from 'config/orderStatusesAutoChange';

// Actions
export const CREATE_ORDER_LINES = 'CREATE_ORDER_LINES';
export const CREATE_ORDER_LINES_SUCCESS = 'CREATE_ORDER_LINES_SUCCESS';
export const CREATE_ORDER_LINES_FAIL = 'CREATE_ORDER_LINES_FAIL';
export const UPDATE_ORDER_LINES = 'UPDATE_ORDER_LINES';
export const UPDATE_ORDER_LINES_SUCCESS = 'UPDATE_ORDER_LINES_SUCCESS';
export const UPDATE_ORDER_LINES_FAIL = 'UPDATE_ORDER_LINES_FAIL';
export const UPDATE_ORDER_LINES_CHECK_NUMBER_SUCCESS = 'UPDATE_ORDER_LINES_CHECK_NUMBER_SUCCESS';
export const TRANSMIT_ORDER_LINES = 'TRANSMIT_ORDER_LINES';
export const TRANSMIT_ORDER_LINES_SUCCESS = 'TRANSMIT_ORDER_LINES_SUCCESS';
export const TRANSMIT_ORDER_LINES_FAIL = 'TRANSMIT_ORDER_LINES_FAIL';
export const DELETE_ORDER_LINES = 'DELETE_ORDER_LINES';
export const DELETE_ORDER_LINES_SUCCESS = 'DELETE_ORDER_LINES_SUCCESS';
export const DELETE_ORDER_LINES_FAIL = 'DELETE_ORDER_LINES_FAIL';

// Reducer
export default function ordersLinesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case CREATE_ORDER_LINES_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const newOrderLine = payload.data.filter(item => item['Order ID'] === dataItem.ID);
        if (newOrderLine.length > 0) {
          return {
            ...dataItem,
            'Order Line': [
              ...dataItem['Order Line'].slice(0, dataItem['Order Line'].length),
              ...newOrderLine.map(item => ({
                ...item,
                'Transmission Method': dataItem['Transmission Method'],
              })),
            ],
          };
        }
        return dataItem;
      });
      return {
        ...state,
        data: newData,
      };
    }
    case UPDATE_ORDER_LINES:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case UPDATE_ORDER_LINES_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const newOrderLine = dataItem['Order Line'].map((orderLineItem) => {
          const newOrderLineItem = payload.data.find(item => item.ID === orderLineItem.ID);
          if (newOrderLineItem) {
            return newOrderLineItem;
          }
          return orderLineItem;
        });

        return {
          ...dataItem,
          'Order Line': newOrderLine,
        };
      });
      return {
        ...state,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case UPDATE_ORDER_LINES_CHECK_NUMBER_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const newOrderLine = dataItem['Order Line'].map((orderLineItem) => {
          const index = payload.data.findIndex(item => item.ID === orderLineItem.ID);
          if (index === -1) {
            return orderLineItem;
          }
          return {
            ...orderLineItem,
            ...payload.data[index]['Check Book Sheet'] ? {
              'Check Book Sheet': payload.data[index]['Check Book Sheet'],
            } : {},
          };
        });

        return {
          ...dataItem,
          'Order Line': newOrderLine,
        };
      });
      return {
        ...state,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case TRANSMIT_ORDER_LINES_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const newOrderLine = dataItem['Order Line'].map((orderLineItem) => {
          const newOrderLineClaim = payload.data[orderLineItem.ID];
          if (!newOrderLineClaim) {
            return orderLineItem;
          }
          return {
            ...orderLineItem,
            'Claim Md Claim': [
              newOrderLineClaim,
              ...orderLineItem['Claim Md Claim'],
            ],
          };
        });

        return {
          ...dataItem,
          'Order Line': newOrderLine,
        };
      });
      return {
        ...state,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INVENTORY_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const newOrderLine = dataItem['Order Line'].map((orderLineItem) => {
          if (orderLineItem.Product.ID === payload.ID) {
            return {
              ...orderLineItem,
              Product: {
                ...orderLineItem.Product,
                ...payload,
              },
            };
          }
          return orderLineItem;
        });

        return {
          ...dataItem,
          'Order Line': newOrderLine,
        };
      });
      return {
        ...state,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case UPDATE_ORDER_LINES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case DELETE_ORDER_LINES_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        const newOrderLine = dataItem['Order Line'].filter(orderLineItem =>
          payload.data.indexOf(orderLineItem.ID) === -1);

        return {
          ...dataItem,
          'Order Line': newOrderLine,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    default:
      return state;
  }
}

// Action Creators
export function createOrderLines(payload) {
  return { type: CREATE_ORDER_LINES, payload };
}

export function createOrderLinesSuccess(payload) {
  return { type: CREATE_ORDER_LINES_SUCCESS, payload };
}

export function createOrderLinesFail(payload) {
  return { type: CREATE_ORDER_LINES_FAIL, payload };
}

export function updateOrderLines(payload) {
  return { type: UPDATE_ORDER_LINES, payload };
}

export function updateOrderLinesSuccess(payload) {
  return { type: UPDATE_ORDER_LINES_SUCCESS, payload };
}

export function updateOrderLinesFail(payload) {
  return { type: UPDATE_ORDER_LINES_FAIL, payload };
}

export function updateOrderLinesCheckNumberSuccess(payload) {
  return { type: UPDATE_ORDER_LINES_CHECK_NUMBER_SUCCESS, payload };
}

export function deleteOrderLines(payload) {
  return { type: DELETE_ORDER_LINES, payload };
}

export function deleteOrderLinesSuccess(payload) {
  return { type: DELETE_ORDER_LINES_SUCCESS, payload };
}

export function deleteOrderLinesFail(payload) {
  return { type: DELETE_ORDER_LINES_FAIL, payload };
}

export function transmitOrderLines(payload) {
  return { type: TRANSMIT_ORDER_LINES, payload };
}

export function transmitOrderLinesSuccess(payload) {
  return { type: TRANSMIT_ORDER_LINES_SUCCESS, payload };
}

export function transmitOrderLinesFail(payload) {
  return { type: TRANSMIT_ORDER_LINES_FAIL, payload };
}

// Requests

export function requestCreateOrderLine(payload) {
  return callApi('workflow/order_line/', 'POST', payload);
}

export function requestUpdateOrderLine(payload) {
  return callApi('workflow/order_line/', 'PATCH', payload);
}

export function requestTransmitOrderLine(payload) {
  return callApi('claim_md/claims/', 'POST', payload);
}

export function requestTransmitOrderLineAttachment(payload) {
  return callApi('claim_md/attachments/', 'POST', payload);
}

export function requestDeleteOrderLine(payload) {
  return callApi(`workflow/order_line/?id=${payload}`, 'DELETE');
}

export function requestCreateOrderLineCheckNumber(payload) {
  return callApi('workflow/check_book_sheet_attach/add/', 'POST', payload);
}

export function requestRemoveOrderLineCheckNumber(payload) {
  return callApi('workflow/check_book_sheet_attach/remove/', 'POST', payload);
}

export function requestUpdateOrderLineCheckNumber(payload) {
  return callApi('workflow/check_book_sheet_attach/update/', 'POST', payload);
}

export function requestCreateOrderLineInsuranceParam(payload) {
  return callApi('workflow/insurance_order_line_param/', 'POST', payload);
}

export function requestUpdateOrderLineInsuranceParam(payload) {
  return callApi('workflow/insurance_order_line_param/', 'PATCH', payload);
}

// Sagas
function* createOrderLinesSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    let mainOrderLineStatusesAutoChange = [];
    let orderLineStatusesAutoChange = [];
    let mainOrderLineRes = [];
    let orderLineRes = [];
    let statusesAutoChangeRes = [];
    let userInfo;
    if (values.mainOrderLine) {
      mainOrderLineRes = yield all(
        values.mainOrderLine.map(item => call(requestCreateOrderLine, item)),
      );
      userInfo = mainOrderLineRes[0].user_info;
      mainOrderLineRes = mainOrderLineRes.map(item => item.data);
      mainOrderLineStatusesAutoChange = mainOrderLineRes.filter(item =>
        orderStatusesAutoChange[item.Status]);
    }
    if (values.orderLine) {
      orderLineRes = yield all(
        values.orderLine.map(item => call(requestCreateOrderLine, item)),
      );
      userInfo = orderLineRes[0].user_info;
      orderLineRes = orderLineRes.map(item => item.data);
      orderLineStatusesAutoChange = orderLineRes.filter(item =>
        orderStatusesAutoChange[item.Status]);
    }
    if (mainOrderLineStatusesAutoChange.length > 0
      || orderLineStatusesAutoChange.length > 0) {
      statusesAutoChangeRes = yield all(
        [...mainOrderLineStatusesAutoChange,
          ...orderLineStatusesAutoChange].map(item => call(requestUpdateOrderLine, {
          ID: item.ID,
          Status: orderStatusesAutoChange[item.Status].autoChangeStatus,
        })),
      );
      userInfo = statusesAutoChangeRes[0].user_info;
      statusesAutoChangeRes = statusesAutoChangeRes.map(item => item.data);
    }
    const res = uniqBy([
      ...statusesAutoChangeRes,
      ...mainOrderLineRes,
      ...orderLineRes,
    ], 'ID');
    yield put(createOrderLinesSuccess({
      data: res,
    }));
    yield put(fetchAccountInformationSuccess(userInfo));
    if (resolve) {
      resolve(res);
    }
  } catch (err) {
    yield put(createOrderLinesFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateOrderLinesSaga(action) {
  const {
    values,
    resolve,
    reject,
    triggerLoading,
    showNotification = true,
  } = action.payload;
  try {
    console.log(values);
    const valuesByOrderID = groupBy(values, 'orderID');
    let checkNumberBulkPayload = {};
    const bulkPayload = Object.keys(valuesByOrderID).reduce((accumulator, currentOrderID) => {
      const {
        orderLineValues,
        orderLineStatusesAutoChangeValues,
        checkNumberValues,
        orderLineInsuranceParamValues,
      } = valuesByOrderID[currentOrderID].reduce((accumulator, currentValue) => {
        let currentOrderLineValues = accumulator.orderLineValues;
        let currentOrderLineStatusesAutoChangeValues = accumulator.orderLineStatusesAutoChangeValues;
        let currentCheckNumberValues = accumulator.checkNumberValues;
        let currentOrderLineInsuranceParamValues = accumulator.orderLineInsuranceParamValues;
        delete currentValue.orderID;
        if (currentValue['Check Book Sheet']) {
          const newCheckNumberValues = Object.keys(currentValue['Check Book Sheet']).map(item => ({
            'Order Line ID': Number(currentValue.ID),
            'Insurance ID': Number(item.replace('insuranceID-', '')),
            ...currentValue['Check Book Sheet'][item]['Check Number']
              ? { 'Check Number': currentValue['Check Book Sheet'][item]['Check Number'] } : {},
            ...currentValue['Check Book Sheet'][item]['Check Book Sheet ID'] !== undefined
              && !currentValue['Check Book Sheet'][item]['Check Number']
              ? { 'Check Book Sheet ID': currentValue['Check Book Sheet'][item]['Check Book Sheet ID'] } : {},
          }));
          currentCheckNumberValues = [
            ...currentCheckNumberValues,
            ...newCheckNumberValues,
          ];
          const currentValueWithoutCheckNumber = Object.assign({}, currentValue);
          delete currentValueWithoutCheckNumber['Check Book Sheet'];
          delete currentValueWithoutCheckNumber.orderLineInsuranceParam;
          if (Object.keys(currentValueWithoutCheckNumber).length > 1) {
            currentOrderLineValues = [
              ...currentOrderLineValues,
              currentValueWithoutCheckNumber,
              ...currentValueWithoutCheckNumber.Status
                && orderStatusesAutoChange[currentValueWithoutCheckNumber.Status] ? [{
                  ID: currentValueWithoutCheckNumber.ID,
                  Status: orderStatusesAutoChange[currentValueWithoutCheckNumber.Status]
                    .autoChangeStatus,
                }] : [],
            ];
          }
        } else if (currentValue.orderLineInsuranceParam) {
          currentOrderLineInsuranceParamValues = [
            ...currentOrderLineInsuranceParamValues,
            ...Object.keys(currentValue.orderLineInsuranceParam).map(item => ({ 
              ...currentValue.orderLineInsuranceParam[item].ID
                ? { ID: currentValue.orderLineInsuranceParam[item].ID } : {
                  'Order Line ID': currentValue.ID,
                  'Insurance ID': Number(item.replace('insuranceID-', '')),
                },
              ...currentValue.orderLineInsuranceParam[item].Reimbs !== undefined
                ? { Reimbs: currentValue.orderLineInsuranceParam[item].Reimbs } : {},
              ...currentValue.orderLineInsuranceParam[item]['Reimbs Date'] !== undefined
                ? { 'Reimbs Date': currentValue.orderLineInsuranceParam[item]['Reimbs Date'] } : {},
              ...currentValue.orderLineInsuranceParam[item]['Check Book Sheet ID'] !== undefined
                ? { 'Check Book Sheet ID': currentValue.orderLineInsuranceParam[item]['Check Book Sheet ID'] } : {},
            })),
          ];
        } else { 
          if (currentValue['Next Workflow Status ID']) {
            currentOrderLineStatusesAutoChangeValues = [
              ...currentOrderLineStatusesAutoChangeValues,
              {
                ID: currentValue.ID,
                'Workflow Status ID': currentValue['Next Workflow Status ID'],
              },
            ];
            delete currentValue['Next Workflow Status ID'];
          }
          currentOrderLineValues = [
            ...currentOrderLineValues,
            currentValue,
          ];
        }
        return {
          orderLineValues: currentOrderLineValues,
          orderLineStatusesAutoChangeValues: currentOrderLineStatusesAutoChangeValues,
          checkNumberValues: currentCheckNumberValues,
          orderLineInsuranceParamValues: currentOrderLineInsuranceParamValues,
        };
      }, {
        orderLineValues: [],
        orderLineStatusesAutoChangeValues: [],
        checkNumberValues: [],
        orderLineInsuranceParamValues: [],
      });
      const prevCheckNumberBulkPayloadLength = Object.keys(checkNumberBulkPayload).length;
      checkNumberBulkPayload = {
        ...checkNumberBulkPayload,
        ...checkNumberValues.reduce((checkNumberAccumulator, checkNumberCurrentValue, checkNumberIndex) => ({
          ...checkNumberAccumulator,
          [prevCheckNumberBulkPayloadLength + checkNumberIndex]: {
            URL: checkNumberCurrentValue['Check Number']
              ? 'workflow/check_book_sheet_attach/add/'
              : 'workflow/check_book_sheet_attach/remove/',
            METHOD: 'POST',
            BODY: checkNumberCurrentValue,
          },
        }), {}),
      };
      return {
        ...accumulator,
        [currentOrderID]: {
          ...orderLineValues
            .reduce((accumulator, currentValue, index) => ({
              ...accumulator,
              [index]: {
                URL: 'workflow/order_line/',
                METHOD: 'PATCH',
                BODY: currentValue,
              },
            }), {}),
          ...orderLineStatusesAutoChangeValues
            .reduce((accumulator, currentValue, index) => ({
              ...accumulator,
              [orderLineValues.length + index]: {
                URL: 'workflow/order_line/',
                METHOD: 'PATCH',
                BODY: currentValue,
              },
            }), {}),
          ...orderLineInsuranceParamValues
            .reduce((accumulator, currentValue, index) => (currentValue.ID && !currentValue.
            Reimbs && !currentValue['Reimbs Date']) ? ({
              ...accumulator,
              [orderLineValues.length + orderLineStatusesAutoChangeValues.length + index]: {
                URL: `workflow/order_line_insurance_param/?id=${currentValue.ID}`,
                METHOD: 'DELETE',
              },
            }) : ({
              ...accumulator,
              [orderLineValues.length + orderLineStatusesAutoChangeValues.length + index]: {
                URL: 'workflow/order_line_insurance_param/',
                METHOD: currentValue.ID ? 'PATCH' : 'POST',
                BODY: currentValue,
              },
            }), {}),
        },
      };
    }, {});
    Object.keys(bulkPayload).forEach((key) => {
      if (Object.keys(bulkPayload[key]).length === 0) {
        delete bulkPayload[key];
      }
    });
    if (bulkPayload && Object.keys(bulkPayload).length > 0) {
      const bulkRes = yield call(requestWorkflowBulk, bulkPayload);
      const orderLinesRes = values.map(orderLine => Object.keys(bulkRes.data).reduce((accumulator, currentValue) =>
        accumulator && accumulator.ID ? accumulator : bulkRes.data[currentValue]['Order Line'].find(resOrderLine => resOrderLine.ID === Number(orderLine.ID))
      , {}));
      yield put(updateOrderLinesSuccess({
        data: orderLinesRes,
        triggerLoading,
      }));
      if (bulkRes.user_info) {
        yield put(fetchAccountInformationSuccess(bulkRes.user_info));
      }
      if (showNotification) {
        yield put(addNotification({
          message: 'Order was successfully updated',
          variant: 'success',
        }));
      }
      if (resolve) {
        resolve(Object.keys(bulkRes.data).reduce((accumulator, key) => ([
          ...accumulator,
          ...bulkRes.data[key]['Order Line'],
        ]), []));
      }
    } else if (checkNumberBulkPayload && Object.keys(checkNumberBulkPayload).length > 0) {
      const res = yield call(requestBulk, checkNumberBulkPayload);
      const checkNumberBulkRes = uniqBy(Object.keys(res.data).map((key) => {
        const item = res.data[key].data;
        if (item.error) {
          return null;
        }
        if (item['Check Number']) {
          const itemPayload = checkNumberBulkPayload[key].BODY;
          return {
            ID: Number(itemPayload['Order Line ID']),
            'Check Book Sheet': checkNumberBulkPayload[key].URL === 'workflow/check_book_sheet_attach/remove/' ? [] : [
              item,
            ],
          };
        }
        return res.data[key].data;
      }).filter(item => item).reverse());
      yield put(updateOrderLinesCheckNumberSuccess({
        data: checkNumberBulkRes,
        triggerLoading,
      }));
      
      if (res.user_info) {
        yield put(fetchAccountInformationSuccess(res.user_info));
      }

      if (showNotification) {
        yield put(addNotification({
          message: 'Order was successfully updated',
          variant: 'success',
        }));
      }
      if (resolve) {
        resolve(checkNumberBulkRes);
      }
    }   
  } catch (err) {
    yield put(updateOrderLinesFail(err.data));
    if (showNotification) {
      yield put(addNotification({
        message: err.data.description || 'Failed to update order',
        variant: 'warning',
      }));
    }
    if (reject) {
      reject({
        ...err.data,
        triggerLoading,
      });
    }
  }
}

function* transmitOrderLinesSaga(action) {
  const {
    values: {
      withClaim = true,
      withAttachment = false,
      ...otherValues
    },
    resolve,
    reject,
  } = action.payload;
  try {
    let claimRes;
    let attachmentRes;
    if (withClaim) {
      claimRes = yield call(requestTransmitOrderLine, {
        'Order Lines ID': otherValues['Order Lines'].map(item => item.ID),
      });
    }
    if (withAttachment) {
      const claimIDs = withClaim
        ? Object.keys(claimRes.data).map(key => claimRes.data[key].ID)
        : otherValues['Order Lines'].filter(item => item.CMN && item.CMN.ID
          && item.Claim && item.Claim[0] && item.Claim[0].ID)
          .map(item => item.Claim[0].ID);
      attachmentRes = yield all(
        claimIDs.map(item => call(requestTransmitOrderLineAttachment, {
          'Claim ID': item,
        })),
      );
      attachmentRes = {
        ...attachmentRes[0],
        data: attachmentRes.reduce((accumulator, currentItem) => ({
          ...accumulator,
          ...currentItem.data,
        }), {}),
      };
    }
    const res = {
      ...claimRes,
      data: claimRes.data.reduce((accumulator, currentValue, index)=> ({
        ...accumulator,
        ...currentValue['Claim Line'].reduce((claimLineAccumulator, claimLineCurrentValue) => ({
          ...claimLineAccumulator,
          [claimLineCurrentValue['Order Line ID']]: currentValue,
        }), {}),
      }), {}),
    };
    yield put(transmitOrderLinesSuccess(res));
    if (withClaim) {
      const {
        sendClaims,
        invalidClaims,
      } = Object.keys(res.data).reduce((accumulator, currentValue) => {
        if (res.data[currentValue].Status.filter(item => item.Status === 'sent').length > 0) {
          return {
            ...accumulator,
            sendClaims: [
              ...accumulator.sendClaims,
              currentValue,
            ],
          };
        }
        return {
          ...accumulator,
          invalidClaims: [
            ...accumulator.invalidClaims,
            currentValue,
          ],
        };
      }, {
        sendClaims: [],
        invalidClaims: [],
      });
      yield put(fetchAccountInformationSuccess(res.user_info));
      yield put(fetchAppInfoSuccess(res.app_info));
      if (sendClaims.length > 0 && invalidClaims.length === 0) {
        yield put(addNotification({
          message: `Order ${formatSinglePlural({
            quantity: sendClaims.length, single: 'line', plural: 'lines',
          })} ${sendClaims.map(item => otherValues['HCPC S Code'][item]).join(', ')} ${formatSinglePlural({
            quantity: sendClaims.length, single: 'was', plural: 'were',
          })} successfully transmitted`,
          variant: 'success',
        }));
      } else if (sendClaims.length === 0 && invalidClaims.length > 0) {
        yield put(addNotification({
          message: `Order ${formatSinglePlural({
            quantity: invalidClaims.length, single: 'line', plural: 'lines',
          })} ${invalidClaims.map(item => otherValues['HCPC S Code'][item]).join(', ')} ${formatSinglePlural({
            quantity: invalidClaims.length, single: 'has', plural: 'have',
          })} validation errors, submission failed. ${uniq(invalidClaims.map(key => res.data[key].error)).join('. ')}`,
          variant: 'warning',
        }));
      } else {
        yield put(addNotification({
          message: `Order ${formatSinglePlural({
            quantity: sendClaims.length, single: 'line', plural: 'lines',
          })} ${sendClaims.map(item => otherValues['HCPC S Code'][item]).join(', ')} ${formatSinglePlural({
            quantity: sendClaims.length, single: 'was', plural: 'were',
          })} successfully transmitted,
            order ${formatSinglePlural({
    quantity: invalidClaims.length, single: 'line', plural: 'lines',
  })} ${invalidClaims.map(item => otherValues['HCPC S Code'][item]).join(', ')} ${formatSinglePlural({
  quantity: invalidClaims.length, single: 'has', plural: 'have',
})} validation errors, submission failed`,
          variant: 'info',
        }));
      }
    } else {
      const {
        sendAttachments,
        invalidAttachments,
      } = Object.keys(res.data).reduce((accumulator, currentValue) => {
        const currentClaim = res.data[currentValue];
        const currentAttachment = currentClaim.Claim.Attachment.reverse()[0];
        if (currentAttachment.Extra && currentAttachment.Extra.error) {
          return {
            ...accumulator,
            invalidAttachments: [
              ...accumulator.invalidAttachments,
              currentClaim,
            ],
          };
        }
        return {
          ...accumulator,
          sendAttachments: [
            ...accumulator.sendAttachments,
            currentClaim,
          ],
        };
      }, {
        sendAttachments: [],
        invalidAttachments: [],
      });
      if (sendAttachments.length > 0 && invalidAttachments.length === 0) {
        yield put(addNotification({
          message: `${formatSinglePlural({
            quantity: sendAttachments.length, single: 'Attachment', plural: 'Attachments',
          })} for ${sendAttachments.map(item => otherValues['HCPC S Code'][item]).join(', ')} ${formatSinglePlural({
            quantity: sendAttachments.length, single: 'was', plural: 'were',
          })} successfully transmitted`,
          variant: 'success',
        }));
      } else if (sendAttachments.length === 0 && invalidAttachments.length > 0) {
        yield put(addNotification({
          message: `${formatSinglePlural({
            quantity: invalidAttachments.length, single: 'Attachment', plural: 'Attachments',
          })} ${formatSinglePlural({
            quantity: invalidAttachments.length, single: 'was', plural: 'were',
          })} not send. ${uniq(invalidAttachments.map(item => item.Claim.Attachment.reverse()[0].Extra.error)).join('. ')}`,
          variant: 'warning',
        }));
      }
    }
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(transmitOrderLinesFail(err.data));
    yield put(addNotification({
      message: err.data.error,
      variant: 'warning',
    }));
    if (reject) {
      reject(err.data.error);
    }
  }
}

function* deleteOrderLinesSaga(action) {
  const {
    values,
    resolve,
    reject,
    showNotification = true,
  } = action.payload;
  try {
    let mainHCPCCodeRes;
    let HCPCCodeRes;
    if (values['HCPC S Code ID']) {
      HCPCCodeRes = yield all(
        values['HCPC S Code ID'].map(item => call(requestDeleteOrderLine, item)),
      );
    }
    if (values['Main HCPC S Code ID']) {
      mainHCPCCodeRes = yield all(
        values['Main HCPC S Code ID'].map(item => call(requestDeleteOrderLine, item)),
      );
    }
    const res = mainHCPCCodeRes || HCPCCodeRes;
    yield put(deleteOrderLinesSuccess({
      data: [
        ...values['Main HCPC S Code ID'] || [],
        ...values['HCPC S Code ID'] || [],
      ],
    }));
    yield put(fetchAccountInformationSuccess(res[0].user_info));
    yield put(fetchAppInfoSuccess(res[0].app_info));
    if (showNotification) {
      yield put(addNotification({
        message: 'Order was successfully updated',
        variant: 'success',
      }));
    }
    if (resolve) {
      resolve([
        ...values['Main HCPC S Code ID'] || [],
        ...values['HCPC S Code ID'] || [],
      ]);
    }
  } catch (err) {
    yield put(deleteOrderLinesFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

export function* ordersLinesSaga() {
  yield takeEvery(CREATE_ORDER_LINES, createOrderLinesSaga);
  yield takeEvery(UPDATE_ORDER_LINES, updateOrderLinesSaga);
  yield takeEvery(TRANSMIT_ORDER_LINES, transmitOrderLinesSaga);
  yield takeEvery(DELETE_ORDER_LINES, deleteOrderLinesSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/ordersLines.js